import dayjs from 'dayjs';
import { ProductEnum } from '@/api';
import type { ProductType } from '@/pages/Availability.page/types';
import type {
  AvailabilitySummary,
  AvailabilitySummaryAccumulator,
  TodoTask,
  TodoTaskAvailabilityRequirement,
  TodoTaskEventVendor,
} from '../types';
import { AVAILABILITY_PRODUCT_ORDER } from './constants';

export const getPopupEventVendors = (task: TodoTask): TodoTaskEventVendor[] =>
  task.workItems.filter(
    (item): item is TodoTaskEventVendor => item.__typename === 'PopupEventVendor'
  );

export const getAvailabilityRequirements = (task: TodoTask): TodoTaskAvailabilityRequirement[] =>
  task.workItems.filter(
    (item): item is TodoTaskAvailabilityRequirement =>
      item.__typename === 'SchedulingAvailabilityRequirement'
  );

export const isAvailabilityProductType = (productType: ProductEnum): productType is ProductType =>
  productType === ProductEnum.POPUP || productType === ProductEnum.DELIVERY;

export const buildAvailabilitySummaries = (tasks: TodoTask[]): AvailabilitySummary[] => {
  const byProduct = new Map<ProductType, AvailabilitySummaryAccumulator>();

  tasks.forEach((task) => {
    getAvailabilityRequirements(task).forEach((req) => {
      if (!isAvailabilityProductType(req.productType)) return;

      const acc: AvailabilitySummaryAccumulator = byProduct.get(req.productType) ?? {
        restaurants: [],
        seen: new Set<string>(),
        expiresAt: task.expiresAt,
      };

      if (new Date(task.expiresAt) < new Date(acc.expiresAt)) {
        acc.expiresAt = task.expiresAt;
      }

      if (!acc.seen.has(req.vendor.id)) {
        acc.seen.add(req.vendor.id);
        acc.restaurants.push({
          vendorId: req.vendor.id,
          internalName: req.vendor.internalName,
        });
      }

      byProduct.set(req.productType, acc);
    });
  });

  return AVAILABILITY_PRODUCT_ORDER.flatMap((productType) => {
    const acc = byProduct.get(productType);
    if (!acc || acc.restaurants.length === 0) return [];
    return [
      {
        productType,
        restaurants: acc.restaurants,
        expiresAt: acc.expiresAt,
      },
    ];
  });
};

export const getEventTaskDate = (task: TodoTask): string => {
  const event = getPopupEventVendors(task)[0];
  if (event) {
    const start = event.popupEvent.eventStartTime;
    const setupMinutes = event.eventLocation?.setupTime ?? 0;
    return dayjs(start).subtract(setupMinutes, 'minute').local().format('YYYY-MM-DD');
  }
  return dayjs(task.dueAt).local().format('YYYY-MM-DD');
};
